import { useShopContext, type Shop } from './ShopContext'

// ShopPicker — full-screen list for merchants with 2+ shops. Rendered by
// App.tsx as a boot-gate between sign-in and the main tabs when
// state.status === 'needs-picker'. Also reachable from Settings later as
// a "Change shop" affordance (planned in 4c.1e).
//
// UX: tap a row → `select(handle)` → state flips to 'ready' → App unmounts
// this and renders the bottom-tab shell. No back button (there's nowhere
// to go back TO — the merchant HAS to pick).

export function ShopPicker() {
  const { state, select } = useShopContext()

  if (state.status !== 'needs-picker' && state.status !== 'ready') {
    // Shouldn't render outside these states; parent gates. Defensive
    // fallback avoids a blank screen if wiring drifts.
    return null
  }

  const shops = state.shops
  const activeHandle = state.status === 'ready' ? state.shop.handle : null

  return (
    <div style={s.wrap}>
      <h1 style={s.h1}>Choose a shop</h1>
      <p style={s.lede}>
        You have {shops.length} shops. Pick one to manage — you can switch
        anytime from Settings.
      </p>
      <div style={s.list}>
        {shops.map(shop => (
          <ShopRow
            key={shop.handle}
            shop={shop}
            active={shop.handle === activeHandle}
            onSelect={() => select(shop.handle)}
          />
        ))}
      </div>
    </div>
  )
}

function ShopRow({ shop, active, onSelect }: { shop: Shop; active: boolean; onSelect: () => void }) {
  return (
    <button style={{ ...s.row, ...(active ? s.rowActive : {}) }} onClick={onSelect}>
      <div style={s.rowMain}>
        <div style={s.rowName}>{shop.brand_name || shop.handle}</div>
        <div style={s.rowSub}>
          {shop.handle}
          {shop.has_shopify && <span style={s.shopifyBadge}>Shopify</span>}
          {shop.role === 'collaborator' && <span style={s.roleBadge}>Collaborator</span>}
          {!shop.has_stripe && !shop.has_shopify && <span style={s.warnBadge}>Payments not connected</span>}
        </div>
      </div>
      <div style={s.rowChevron}>{active ? '✓' : '›'}</div>
    </button>
  )
}

const s = {
  wrap: {
    padding: 'max(24px, env(safe-area-inset-top)) 20px 24px',
    minHeight: '100vh',
    background: '#faf9f6',
    color: '#1a1a18',
    boxSizing: 'border-box' as const,
  },
  h1: {
    fontSize: 26,
    fontWeight: 700,
    marginTop: 0,
    marginBottom: 8,
  },
  lede: {
    fontSize: 14,
    color: '#5a5a55',
    lineHeight: 1.55,
    marginBottom: 24,
  },
  list: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 10,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '16px 18px',
    background: '#ffffff',
    border: '1px solid #e5e5e0',
    borderRadius: 12,
    cursor: 'pointer',
    textAlign: 'left' as const,
    fontFamily: 'inherit',
  },
  rowActive: {
    border: '2px solid #2a6a3a',
    padding: '15px 17px',   // compensate for the 2px border so height doesn't jump
  },
  rowMain: {
    flex: 1,
    minWidth: 0,
  },
  rowName: {
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 4,
  },
  rowSub: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: 6,
    fontSize: 12,
    color: '#8a8a80',
    fontFamily: '"SF Mono", Consolas, monospace',
  },
  shopifyBadge: {
    padding: '1px 6px',
    background: '#95bf47',   // Shopify brand green
    color: '#ffffff',
    borderRadius: 4,
    fontSize: 10,
    fontFamily: 'inherit',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.3,
  },
  roleBadge: {
    padding: '1px 6px',
    background: '#f0efe9',
    color: '#5a5a55',
    borderRadius: 4,
    fontSize: 10,
    fontFamily: 'inherit',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.3,
  },
  warnBadge: {
    padding: '1px 6px',
    background: '#3a2f1e',
    color: '#e5b880',
    borderRadius: 4,
    fontSize: 10,
    fontFamily: 'inherit',
    fontWeight: 600,
  },
  rowChevron: {
    fontSize: 20,
    color: '#8a8a80',
    fontWeight: 300,
    flexShrink: 0,
  },
} as const
